import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DictionaryPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('user');
  const [userDictionary, setUserDictionary] = useState([]);
  const [orgDictionary, setOrgDictionary] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [currentEntry, setCurrentEntry] = useState({
    term: '',
    expansion: '',
    phonetic: '',
    category: 'abbreviation',
    auto_expand: true,
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadDictionaries();
  }, []);

  const loadDictionaries = async () => {
    try {
      const userResp = await api.get('/api/stt/dictionary/user');
      setUserDictionary(userResp.data);

      const orgResp = await api.get('/api/stt/dictionary/organization');
      setOrgDictionary(orgResp.data);
    } catch (error) {
      console.error('Failed to load dictionaries:', error);
      toast.error('Failed to load dictionaries');
    }
  };

  const handleAddEntry = async () => {
    if (activeTab === 'organization') {
      toast.error('Organisation terms can be edited from the Admin page.');
      return;
    }

    if (!currentEntry.term.trim() || !currentEntry.expansion.trim()) {
      toast.error('Term and expansion are required');
      return;
    }

    try {
      const endpoint = activeTab === 'user' 
        ? '/api/stt/dictionary/user'
        : '/api/stt/dictionary/organization';

      if (isEditing) {
        await api.put(`${endpoint}/${currentEntry.id}`, currentEntry);
        toast.success('Entry updated');
      } else {
        await api.post(endpoint, currentEntry);
        toast.success('Entry added');
      }

      setShowAddDialog(false);
      setCurrentEntry({ term: '', expansion: '', phonetic: '', category: 'abbreviation', auto_expand: true });
      setIsEditing(false);
      loadDictionaries();
    } catch (error) {
      console.error('Failed to save entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleEditEntry = (entry) => {
    setCurrentEntry({ ...entry, auto_expand: entry.auto_expand ?? true });
    setIsEditing(true);
    setShowAddDialog(true);
  };

  const handleDeleteEntry = async (entryId) => {
    if (activeTab !== 'user') {
      toast.error('Organisation terms can be edited from the Admin page.');
      return;
    }

    if (!window.confirm('Delete this entry?')) return;

    try {
      const endpoint = activeTab === 'user' 
        ? `/api/stt/dictionary/user/${entryId}`
        : `/api/stt/dictionary/organization/${entryId}`;

      await api.delete(endpoint);
      toast.success('Entry deleted');
      loadDictionaries();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const filteredDictionary = () => {
    const dict = activeTab === 'user' ? userDictionary : orgDictionary;
    
    let filtered = dict;
    
    if (categoryFilter !== 'all' && activeTab === 'organization') {
      filtered = filtered.filter(e => e.category === categoryFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(e => 
        e.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.expansion.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const categories = ['abbreviation', 'medication', 'anatomy', 'procedure'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          📚 Medical Dictionary
        </h1>
        <p className="text-gray-600">
          Manage medical terms for improved speech recognition
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('user')}
              className={`px-4 py-2 rounded-md font-medium ${
                activeTab === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              My Dictionary ({userDictionary.length})
            </button>
            <button
              onClick={() => setActiveTab('organization')}
              className={`px-4 py-2 rounded-md font-medium ${
                activeTab === 'organization'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Organization ({orgDictionary.length})
            </button>
          </div>

          <button
            onClick={() => {
              setCurrentEntry({ term: '', expansion: '', phonetic: '', category: 'abbreviation', auto_expand: true });
              setIsEditing(false);
              setShowAddDialog(true);
            }}
            disabled={activeTab === 'organization'}
            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Entry
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search terms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {activeTab === 'organization' && (
            <div className="w-full sm:w-auto">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {activeTab === 'organization' && (
          <div className="mt-3 text-sm text-blue-700 bg-blue-50 p-2 rounded">
            ℹ️ Organisation vocabulary is read-only in this view.
            {user?.is_admin ? (
              <> Manage entries from the <Link to="/admin" className="text-blue-600 underline">Admin page</Link>.</>
            ) : (
              ' Contact an administrator to request updates.'
            )}
          </div>
        )}
      </div>

      {/* Dictionary Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Term
                </th>
                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expansion
                </th>
                {activeTab === 'organization' && (
                  <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                )}
                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phonetic
                </th>
                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auto Expand
                </th>
                <th className="px-3 py-2 sm:px-6 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDictionary().map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {entry.term}
                  </td>
                  <td className="px-3 py-3 sm:px-6 sm:py-4 text-sm text-gray-700">
                    {entry.expansion}
                  </td>
                  {activeTab === 'organization' && (
                    <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {entry.category}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-500">
                    {entry.phonetic || '-'}
                  </td>
                  <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${entry.auto_expand ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {entry.auto_expand ? 'On' : 'Off'}
                    </span>
                  </td>
                  <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                    {activeTab === 'user' && (
                      <>
                        <button
                          onClick={() => handleEditEntry(entry)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}

              {filteredDictionary().length === 0 && (
                <tr>
                  <td colSpan={activeTab === 'organization' ? 6 : 5} className="px-6 py-8 text-center text-gray-500">
                    No entries found. {activeTab === 'user' ? 'Add your first term!' : 'Try adjusting your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {isEditing ? 'Edit Entry' : 'Add New Entry'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Term *
                </label>
                <input
                  type="text"
                  value={currentEntry.term}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, term: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., BP"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expansion *
                </label>
                <input
                  type="text"
                  value={currentEntry.expansion}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, expansion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., blood pressure"
                />
              </div>

              {activeTab === 'organization' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={currentEntry.category}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phonetic (Optional)
                </label>
                <input
                  type="text"
                  value={currentEntry.phonetic}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, phonetic: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., bee-pee"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Auto Expand
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!currentEntry.auto_expand}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, auto_expand: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">
                    Expand this term automatically during realtime corrections
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleAddEntry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {isEditing ? 'Update' : 'Add'}
              </button>
              <button
                onClick={() => {
                  setShowAddDialog(false);
                setCurrentEntry({ term: '', expansion: '', phonetic: '', category: 'abbreviation', auto_expand: true });
                  setIsEditing(false);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DictionaryPage;
